import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import ManifestoSection from './components/ManifestoSection';
import ArticleCard from './components/ArticleCard';
import CommunityStats from './components/CommunityStats';
import SearchBar from './components/SearchBar';
import Footer from './components/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Pagination from '../../components/ui/Pagination';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import circlesService from '../../services/circlesService';
import { useAuth } from '../../contexts/AuthContext';
import { getRootCircles, getChildCircles } from '../../utils/circles';

const Homepage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeCircle, setActiveCircle] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());

  // Data State
  const [articles, setArticles] = useState([]);
  const [circles, setCircles] = useState([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [loading, setLoading] = useState(false);

  // UI State
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredCircle, setHoveredCircle] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [contentType, setContentType] = useState('articles');
  const [expandedPoetry, setExpandedPoetry] = useState(false);
  const [expandedLiterature, setExpandedLiterature] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoverCardTop, setHoverCardTop] = useState(0);
  const [hoverCardRight, setHoverCardRight] = useState(0);
  const gridRef = useRef(null);
  const topRef = useRef(null);
  const circleRefs = useRef({});
  const prevPathRef = useRef(location?.pathname);
  const isInitialMountRef = useRef(true);

  const ITEMS_PER_PAGE = 12;

  // Reset search when navigating to homepage from another page
  useEffect(() => {
    const prev = prevPathRef.current;
    const curr = location?.pathname ?? '';
    prevPathRef.current = curr;
    const isHome = (p) => p === '/' || p === '/homepage';
    if (isHome(curr) && !isHome(prev)) {
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, [location?.pathname]);


  const handleHover = (id, e) => {
    setHoveredCircle(id);
    if (e && e?.currentTarget) {
      const rect = e?.currentTarget?.getBoundingClientRect();
      setHoverCardTop(rect?.top + rect?.height / 2);
      setHoverCardRight(window.innerWidth - rect?.left + 20);
    }
  };

  const handleCircleHover = (circleId, element) => {
    setHoveredCircle(circleId);
    setHoveredElement(element);
  };

  const handleCircleLeave = () => {
    setHoveredCircle(null);
    setHoveredElement(null);
  };

  // Scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* Global Scrollbar */
      ::-webkit-scrollbar {
        width: 12px;
        height: 12px;
      }
      ::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 6px;
      }
      ::-webkit-scrollbar-thumb {
        background: #C5A059;
        border-radius: 6px;
        border: 3px solid #f1f1f1;
      }
      ::-webkit-scrollbar-thumb:hover {
        background: #A68548;
      }

      /* Sidebar Scrollbar - Specific Class */
      .sidebar-scroll::-webkit-scrollbar {
        width: 6px;
      }
      .sidebar-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .sidebar-scroll::-webkit-scrollbar-thumb {
        background: #C5A059;
        border-radius: 3px;
        border: none;
      }
      /* Firefox support */
      .sidebar-scroll {
        scrollbar-width: thin;
        scrollbar-color: #C5A059 transparent;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [poetrySubcategories, setPoetrySubcategories] = useState([]);
  const [poetryCircle, setPoetryCircle] = useState(null);

  // Fetch circles from backend - homepage shows poetry as subcategory of literature
  useEffect(() => {
    const fetchCircles = async () => {
      try {
        const { data } = await circlesService.getCircles();
        const raw = Array.isArray(data) ? data : [];
        // Exclude poetry from root; it appears under literature
        const rootCircles = getRootCircles(raw)
          .filter((c) => c.slug !== 'poetry')
          .map((c) => ({
            ...c,
            id: c.slug,
            originalId: c.id,
            hasChildren: c.hasChildren || raw.some((ch) => ch.parentId === c.id) || (c.slug === 'literature'),
          }));
        setCircles(rootCircles);
        const poetry = raw.find((c) => c.slug === 'poetry');
        setPoetryCircle(poetry ? { ...poetry, id: poetry.slug } : null);
        setPoetrySubcategories(getChildCircles(raw, 'poetry'));
        circleRefs.current.allCircles = raw.map((c) => ({ ...c, id: c.slug, parentSlug: c.parentId ? raw.find((p) => p.id === c.parentId)?.slug : null }));
      } catch (error) {
        console.error('Failed to fetch circles:', error);
      }
    };
    fetchCircles();
  }, []);



  // activeCircle is the slug - pass directly to API
  const getCircleSlug = (circleId) => circleId;

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          pageSize: ITEMS_PER_PAGE,
          search: searchQuery,
          circleSlug: activeCircle !== 'all' ? getCircleSlug(activeCircle) : undefined,
          sortBy: 'trending' // Defaulting to trending or latest
        };
        const { data } = await articlesService.getArticles(params);
        setArticles(data.items || []);
        setTotalArticles(data.totalCount || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [activeCircle, searchQuery, currentPage, contentType]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBookmarkIds = async () => {
      try {
        const { data } = await articlesService.getMyBookmarkIds();
        setBookmarkedIds(new Set(data));
      } catch (err) {
        console.error('Failed to fetch bookmark IDs:', err);
      }
    };
    fetchBookmarkIds();
  }, [isAuthenticated]);

  const handleBookmarkToggle = async (articleId) => {
    if (!isAuthenticated) return;
    try {
      const wasBookmarked = bookmarkedIds.has(articleId);
      if (wasBookmarked) {
        await engagementService.removeBookmark(articleId);
        setBookmarkedIds(prev => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
      } else {
        await engagementService.bookmarkArticle(articleId);
        setBookmarkedIds(prev => new Set(prev).add(articleId));
      }
    } catch (err) {
      console.error('Bookmark toggle failed:', err);
    }
  };

  // Handle URL Params for discussions filter and circle selection
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params?.get('filter');
    const circleParam = params?.get('circle');
    if (filter === 'discussions') {
      setContentType('discussions');
    } else {
      setContentType('articles');
    }
    if (circleParam) {
      const isPoetryOrSub = (id) => id === 'poetry' || ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(id);
      setActiveCircle(circleParam);
      setExpandedPoetry(isPoetryOrSub(circleParam));
      setExpandedLiterature(circleParam === 'literature' || isPoetryOrSub(circleParam));
    }
  }, [location?.search]);

  const handleFilterChange = (circleId, e) => {
    if (e) e.preventDefault();
    const isPoetryOrSub = (id) => id === 'poetry' || ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(id);
    if (activeCircle === circleId || (circleId === 'poetry' && isPoetryOrSub(activeCircle)) || (circleId === 'literature' && activeCircle === 'literature')) {
      setActiveCircle('all');
      setExpandedPoetry(false);
      setExpandedLiterature(false);
    } else {
      setActiveCircle(circleId);
      setExpandedPoetry(isPoetryOrSub(circleId));
      setExpandedLiterature(circleId === 'literature' || isPoetryOrSub(circleId));
    }
    // Force contentMode back to 'ARTICLES'
    setContentType('articles');
    const params = new URLSearchParams(location.search);
    params?.delete('filter');
    if (circleId === 'all') {
      params?.delete('circle');
    } else {
      params?.set('circle', circleId);
    }
    navigate({ search: params?.toString() }, { replace: true });
    setCurrentPage(1);
  };

  const handleContentTypeChange = (type) => {
    setContentType(type);
    setActiveCircle('all');
    const params = new URLSearchParams(location.search);
    if (type === 'discussions') {
      params?.set('filter', 'discussions');
    } else {
      params?.delete('filter');
    }
    navigate({ search: params?.toString() }, { replace: true });
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber, e) => {
    if (e) {
      e?.preventDefault();
      e?.currentTarget?.blur();
    }
    setCurrentPage(pageNumber);
  };

  // Scroll to articles area when page changes (fixes "Next" ending up below hero section)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (topRef?.current) {
      topRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [currentPage]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <div className="hidden lg:block">
        <Header />
      </div>

      <div className="flex lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#F9F7F2]/95 backdrop-blur-sm z-50 px-4 justify-between items-center mt-0 pt-20">
        <div className="flex items-center gap-2">
          <Image
            src="/assets/images/header_logo-1769143036795.png"
            alt="شعار دوائر"
            className="h-8 object-contain"
            style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(32%) saturate(567%) hue-rotate(358deg) brightness(92%) contrast(87%)' }} />
          <div className="flex flex-col justify-center">
            <h1 className="font-bold text-[32px]" style={{ fontFamily: 'Aref Ruqaa, serif', color: '#C5A059' }}>
              دوائر
            </h1>
            <p className="whitespace-nowrap text-xs" style={{ color: '#5A5A5A' }}>
              نغرّد داخل السِرب؛ آملين أن تنبُتَ للجميع أجنحة.
            </p>
          </div>
        </div>
        <button onClick={() => setIsMobileDrawerOpen(true)} className="flex items-center justify-center w-10 h-10" aria-label="القائمة">
          <Icon name="Menu" size={24} color="#C5A059" />
        </button>
      </div>

      {isMobileDrawerOpen &&
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setIsMobileDrawerOpen(false)}>
          <div className="absolute top-0 right-0 bottom-0 w-80 bg-[#F9F7F2] shadow-2xl overflow-y-auto scrollbar-hide animate-slideInRight" onClick={(e) => e?.stopPropagation()}>
            <div className="flex justify-end p-4">
              <button onClick={() => setIsMobileDrawerOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5" aria-label="إغلاق">
                <Icon name="X" size={24} color="#5A5A5A" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4 text-center">
              <a href="/" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>الرئيسية</a>
              <a href="/about-dawaaer" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>عن دوائر</a>
              <a href="/about" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>من نحن؟</a>
              <a href="/policy" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>سياستنا</a>
            </div>
            <div className="mx-6 my-4 border-t border-[#C5A059]/30" />
            <div className="px-6 py-4">
              <h3 className="text-lg font-bold mb-4 text-right" style={{ fontFamily: 'Aref Ruqaa, serif', color: '#2C2B29' }}>دوائر</h3>
              <div className="space-y-2">
                {circles?.map((circle) =>
                  <div key={circle?.id}>
                    <button
                      onClick={(e) => {
                        handleFilterChange(circle?.id, e);
                        setIsMobileDrawerOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-h-[44px] ${activeCircle === circle?.id ? 'bg-[#C5A059]/15 text-[#C5A059]' : 'text-[#2C2B29] hover:bg-[#C5A059]/10'}`}
                      style={{ fontFamily: 'Amiri, serif' }}>
                      <Icon name={circle?.icon} size={20} color={activeCircle === circle?.id ? '#C5A059' : circle?.color} />
                      <span className="flex-1 text-right text-base">{circle?.name}</span>
                    </button>
                    {circle?.id === 'literature' && poetryCircle &&
                      <div className="pr-4 mt-1 space-y-1">
                        <button
                          onClick={(e) => { handleFilterChange(poetryCircle?.id, e); setIsMobileDrawerOpen(false); }}
                          className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-all min-h-[40px] ${activeCircle === poetryCircle?.id || ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(activeCircle) ? 'bg-[#C5A059]/12 text-[#C5A059]' : 'text-[#555] hover:bg-[#C5A059]/8'}`}
                          style={{ fontFamily: 'Amiri, serif', fontSize: '14px' }}>
                          <span>{poetryCircle?.name}</span>
                        </button>
                        {poetrySubcategories?.map((subcat) => (
                          <button
                            key={subcat?.slug}
                            onClick={(e) => { handleFilterChange(subcat?.slug, e); setIsMobileDrawerOpen(false); }}
                            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-all min-h-[36px] ${activeCircle === subcat?.slug ? 'bg-[#C5A059]/10 text-[#C5A059]' : 'text-[#666] hover:bg-[#C5A059]/6'}`}
                            style={{ fontFamily: 'Amiri, serif', fontSize: '13px' }}>
                            <span>{subcat?.name}</span>
                          </button>
                        ))}
                      </div>
                    }
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4">
              <button
                onClick={() => {
                  navigate('/discussions-feed');
                  setIsMobileDrawerOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-h-[44px] bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20"
                style={{ fontFamily: 'Amiri, serif' }}>
                <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                  <Icon name="Users" size={20} color="#C5A059" />
                </div>
                <span className="flex-1 text-right text-base">حلقات نقاشية</span>
              </button>
            </div>
            <div className="px-6 py-6 mt-auto">
              <button
                onClick={() => navigate('/creator-studio')}
                className="w-full bg-[#C5A059] text-white py-4 rounded-full font-medium text-lg min-h-[44px]"
                style={{ fontFamily: 'Amiri, serif' }}>
                اترك أثراً
              </button>
            </div>
          </div>
        </div>
      }

      <main className="pt-16 lg:pt-20">
        <div ref={topRef} />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10 lg:py-12 mt-[7px] mb-[-43px] pb-[25px]">
          <div className="flex flex-col lg:flex-row gap-16 mt-0 pb-0 -mb-14">

            <aside className="hidden lg:block w-[300px] flex-shrink-0" style={{ position: 'relative', zIndex: 100 }}>
              <div
                className="sticky flex flex-col"
                style={{
                  top: '2rem',
                  height: 'calc(100vh - 2rem)',
                  overflow: 'hidden'
                }}>
                <div className="flex-shrink-0 mt-[134px]">
                  <div className="mb-6">
                    <SearchBar onSearch={handleSearch} value={searchQuery} />
                  </div>
                  <div className="my-6"></div>
                  <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Aref Ruqaa, serif', color: 'var(--color-foreground)' }}>
                    دوائر
                  </h2>
                </div>
                <div className="flex-1 overflow-y-auto sidebar-scroll" style={{ direction: 'ltr' }}>
                  <div className="space-y-2" style={{ direction: 'rtl' }}>
                    {circles?.map((circle) =>
                      <div key={circle?.id}>
                        <div
                          className="relative"
                          style={{ overflow: 'visible' }}
                          onMouseEnter={(e) => handleHover(circle?.id, e)}
                          onMouseLeave={() => setHoveredCircle(null)}>
                          <button
                            onClick={(e) => handleFilterChange(circle?.id, e)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition-all duration-300 ${activeCircle === circle?.id ? 'text-[#C5A059]' : 'text-[var(--color-foreground)]'}`}
                            style={{
                              fontFamily: 'Amiri, serif',
                              fontWeight: 400,
                              backgroundColor: activeCircle === circle?.id ? 'rgba(197, 160, 89, 0.12)' : 'transparent'
                            }}>
                            <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                              <Icon name={circle?.icon} size={20} color={activeCircle === circle?.id ? '#C5A059' : circle?.color} />
                            </div>
                            <span className="flex-1 text-right text-base font-medium">{circle?.name}</span>
                            <div className="mr-auto flex items-center gap-2">
                              {activeCircle === circle?.id && <Icon name="X" size={14} color="#C5A059" />}
                              {circle?.hasChildren && <Icon name={circle?.id === 'literature' && expandedLiterature ? "ChevronDown" : "ChevronRight"} size={16} color={activeCircle === circle?.id ? '#C5A059' : '#999'} />}
                            </div>
                          </button>
                        </div>

                        {circle?.id === 'literature' && expandedLiterature && poetryCircle &&
                          <div className="overflow-hidden transition-all duration-500 pr-4" style={{ animation: 'slideDown 500ms ease-out' }}>
                            <div className="pr-8 mt-2 space-y-1">
                              <div
                                className="relative"
                                style={{ overflow: 'visible' }}
                                onMouseEnter={(e) => handleHover(poetryCircle?.id, e)}
                                onMouseLeave={() => setHoveredCircle(null)}>
                                <button
                                  onClick={(e) => handleFilterChange(poetryCircle?.id, e)}
                                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${activeCircle === poetryCircle?.id || ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(activeCircle) ? 'text-[#C5A059]' : 'text-[#666]'}`}
                                  style={{ fontFamily: 'Amiri, serif', fontSize: '14px', backgroundColor: activeCircle === poetryCircle?.id || ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(activeCircle) ? 'rgba(197, 160, 89, 0.08)' : 'transparent' }}>
                                  <span className="flex-1 text-right">{poetryCircle?.name}</span>
                                  {poetrySubcategories?.length > 0 && (
                                    <span onClick={(e) => { e.stopPropagation(); setExpandedPoetry((p) => !p); }} className="cursor-pointer p-1">
                                      <Icon name={expandedPoetry ? "ChevronDown" : "ChevronRight"} size={14} color={activeCircle === poetryCircle?.id || ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(activeCircle) ? '#C5A059' : '#999'} />
                                    </span>
                                  )}
                                </button>
                              </div>
                              {expandedPoetry && poetrySubcategories?.length > 0 &&
                                <div className="pr-4 mt-1 space-y-1">
                                  {poetrySubcategories?.map((subcat) => (
                                    <button
                                      key={subcat?.slug || subcat?.id}
                                      onClick={(e) => handleFilterChange(subcat?.slug, e)}
                                      onMouseEnter={(e) => handleHover(subcat?.slug, e)}
                                      onMouseLeave={() => setHoveredCircle(null)}
                                      className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${activeCircle === subcat?.slug ? 'text-[#C5A059]' : 'text-[#666]'}`}
                                      style={{ fontFamily: 'Amiri, serif', fontSize: '13px', backgroundColor: activeCircle === subcat?.slug ? 'rgba(197, 160, 89, 0.08)' : 'transparent' }}>
                                      <span>{subcat?.name}</span>
                                    </button>
                                  ))}
                                </div>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0 pt-4 mt-4" style={{ borderTop: '1px solid rgba(197, 160, 89, 0.2)' }}>
                  <button
                    onClick={() => navigate('/discussions-feed')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-full transition-all duration-300 text-[var(--color-foreground)] hover:bg-[rgba(197,160,89,0.12)]"
                    style={{ fontFamily: 'Amiri, serif', fontWeight: 400, backgroundColor: 'rgba(249, 247, 242, 0.5)', fontSize: '15px' }}>
                    <Icon name="Users" size={20} color="#8B6914" />
                    <span className="text-xl">حلقات نقاشية</span>
                  </button>
                </div>
              </div>
            </aside>

            <div className="flex-1 pt-[72px]" style={{ position: 'relative', zIndex: 1 }}>
              <div className="lg:hidden mb-6" id="mobile-search-container">
                <SearchBar onSearch={handleSearch} value={searchQuery} />
              </div>

              <ManifestoSection activeCircle={activeCircle} />

              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
                </div>
              ) : articles.length > 0 ? (
                <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles.map((article) =>
                    <ArticleCard
                      key={article?.id}
                      article={article}
                      isDiscussion={contentType === 'discussions'}
                      isBookmarked={bookmarkedIds.has(article?.id)}
                      onBookmarkToggle={handleBookmarkToggle}
                      isAuthenticated={isAuthenticated}
                    />
                  )}
                </div>
              ) : (
                <div className="text-center py-12 md:py-16 lg:py-20">
                  <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="text-xl md:text-2xl font-bold text-[var(--color-foreground)] mb-2">
                    لم يتم العثور على نتائج
                  </h3>
                </div>
              )}

              {articles?.length > 0 &&
                <div className="w-full flex justify-center mt-12 mb-16 relative z-20">
                  <Pagination
                    postsPerPage={ITEMS_PER_PAGE}
                    totalPosts={totalArticles}
                    paginate={handlePageChange}
                    currentPage={currentPage} />
                </div>
              }
            </div>
          </div>
        </div>

        <div style={{ marginTop: '100px', marginBottom: '100px' }}>
          <HeroSection />
        </div>

        <div className="relative block" style={{ height: '256px', overflow: 'visible', marginTop: '60px', marginBottom: '60px' }}>
          <p className="absolute top-[20%] left-1/2 -translate-x-1/2 text-center text-[#C5A059] font-['Amiri'] font-bold text-xl opacity-55 -rotate-3 w-[90%] leading-relaxed pointer-events-none lg:hidden">
            وَمَا زال فِي الدَّرب دربٌ. وما زال في الدرب مُتّسعٌ للرَّحيلْ
          </p>
          <div className="hidden lg:block">
            <div className="absolute pointer-events-none" style={{ top: '0', right: '10%', zIndex: 0, transform: 'rotate(-3deg)' }}>
              <p className="font-medium" style={{ fontFamily: 'Aref Ruqaa, serif', fontSize: '2.5rem', color: '#C5A059', opacity: 0.35, letterSpacing: '1px', lineHeight: '1.4', textAlign: 'right' }}>
                وَمَا زال فِي الدَّرب دربٌ. وما زال في الدرب مُتّسعٌ للرَّحيلْ
              </p>
            </div>
            <div className="absolute pointer-events-none" style={{ bottom: '2%', left: '43%', zIndex: 0, transform: 'translateX(-50%) rotate(-1deg)' }}>
              <p className="font-medium" style={{ fontFamily: 'Aref Ruqaa, serif', fontSize: '2.2rem', color: '#C5A059', opacity: 0.35, letterSpacing: '1px', lineHeight: '1.4', textAlign: 'center' }}>
                إذا أراد أن يُظهرَ فضلَه عليكَ.. خَلَقَ.. ونَسَبَ إليك!
              </p>
            </div>
            {/* Added more quotes from snippet */}
            <div className="absolute pointer-events-none" style={{ top: '40%', left: '25%', zIndex: 0, transform: 'rotate(-4deg)' }}>
              <p className="font-medium" style={{ fontFamily: 'Aref Ruqaa, serif', fontSize: '2rem', color: '#C5A059', opacity: 0.35, letterSpacing: '1px', lineHeight: '1.4' }}>
                الشارع لمين؟ الشارع لنا!
              </p>
            </div>
          </div>
        </div>

        <CommunityStats />
      </main>
      <Footer />
      {showScrollTop &&
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 left-6 md:bottom-8 md:left-8 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[var(--color-accent)] hover:bg-[var(--color-primary)] shadow-warm-lg transition-all duration-300 flex items-center justify-center z-50 group"
          aria-label="العودة للأعلى">
          <Icon name="ArrowUp" size={24} color="var(--color-accent-foreground)" />
        </button>
      }

      <div
        className="hidden lg:block fixed z-[9999] pointer-events-none transition-all duration-300 ease-out"
        style={{
          right: hoverCardRight,
          top: hoverCardTop,
          transform: 'translateY(-50%)',
          opacity: hoveredCircle ? 1 : 0
        }}>
        <div className="bg-[#F9F7F2] shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-3xl px-8 py-6 max-w-sm">
          <p className="text-[#5A5A5A] text-lg font-['Amiri'] leading-relaxed text-right m-0">
            {circles.find(c => c.id === hoveredCircle)?.subtext ||
             (poetryCircle?.id === hoveredCircle ? poetryCircle?.subtext : null) ||
             poetrySubcategories.find(c => c.slug === hoveredCircle)?.subtext ||
             ''}
          </p>
        </div>
      </div>
    </div>);
};

export default Homepage;
